import { Button, Group, TextInput } from "@mantine/core"
import { hasLength } from "@mantine/form"
import { modals } from "@mantine/modals"
import { useTranslation } from "react-i18next"
import { useAPIResource, useAuth, useFormData, usePermissions } from "@/hooks"
import { hasAdmin, queryKeys, userAPI } from "@/utils"
import { ModalControls } from "../controls"
import {
  AppDateInput,
  CitySelect,
  CompanyLocaleSelect,
  PositionInput,
  RoleSelect,
  TeamsSelect,
  UserSelect,
} from "../forms"
import { AdminOnly, AppLoader, ManagerOnly, Modal404 } from "../utils"
import { UserDelete } from "./UserDelete"

const fieldsToEdit = [
  "full_name",
  "position",
  "team_ids",
  "manager_id",
  "city_id",
  "locale",
  "role",
  "birthday",
  "dtstart",
] as const

export interface UserEditProps {
  userId: string
  allowDelete?: boolean
  only?: (typeof fieldsToEdit)[number][]
}

export function UserEdit({ userId, allowDelete, only }: UserEditProps) {
  const { t } = useTranslation()
  const { isAdmin, isSectionAdmin } = usePermissions({ activity: "people" })
  const { user: curUser } = useAuth()

  const {
    data: user,
    isPending,
    update,
  } = useAPIResource(userAPI, {
    id: userId,
    // Self edits must land in auth, everything reads the current user from there.
    invalidate: userId === curUser?.id ? [...queryKeys.auth, userAPI.url()] : undefined,
  })
  const form = useFormData({
    data: user,
    validate: {
      full_name: hasLength({ min: 2 }, t("Name is too short")),
    },
  })

  const fields = only ?? fieldsToEdit

  if (isPending) return <AppLoader />
  if (!user) return <Modal404 />

  return (
    <>
      <Group mb="md">
        {/* Full name */}
        {fields.includes("full_name") && <TextInput label={t("Name")} w="100%" {...form.getInputProps("full_name")} />}

        {/* Position */}
        {fields.includes("position") && (
          <ManagerOnly teamIds={user.team_ids}>
            <PositionInput label={t("Position")} w="100%" {...form.getInputProps("position")} />
          </ManagerOnly>
        )}

        {/* Team and manager */}
        {(isAdmin || (isSectionAdmin && !hasAdmin(user))) && (
          <>
            {fields.includes("team_ids") && <TeamsSelect {...form.getInputProps("team_ids")} />}
            {fields.includes("manager_id") && (
              <UserSelect
                w="100%"
                clearable
                label={t("Supervisor")}
                placeholder={t("Select a supervisor")}
                {...form.getInputProps("manager_id")}
              />
            )}
          </>
        )}

        {/* City */}
        {fields.includes("city_id") && <CitySelect {...form.getInputProps("city_id")} />}

        {/* Locale */}
        {fields.includes("locale") && <CompanyLocaleSelect w="100%" {...form.getInputProps("locale")} />}

        {/* Role */}
        {fields.includes("role") && (
          <AdminOnly>
            <RoleSelect {...form.getInputProps("role")} />
          </AdminOnly>
        )}

        {/* Birthday */}
        {fields.includes("birthday") && (
          <AppDateInput label={t("Birthday")} resultFormat="string" clearable {...form.getInputProps("birthday")} />
        )}

        {/* Work Start */}
        {fields.includes("dtstart") && (
          <AppDateInput label={t("Work Start")} resultFormat="string" clearable {...form.getInputProps("dtstart")} />
        )}
      </Group>
      <ModalControls
        disabled={!form.isValid()}
        close={() => {
          form.reset()
          modals.close(UserEdit.modalId(user.id))
        }}
        onSubmit={form.onSubmit((formValues) => {
          const values = Object.fromEntries(
            Object.entries(formValues || {}).filter(([key]) => fields.includes(key as (typeof fieldsToEdit)[number])),
          )
          if (Object.keys(values).length > 0) {
            update({ ...values, id: user.id })
          }
          modals.close(UserEdit.modalId(user.id))
        })}
      >
        {allowDelete && (
          <AdminOnly>
            <UserDelete user={user}>
              <Button color="red" onClick={() => modals.closeAll()}>
                {t("Delete")}
              </Button>
            </UserDelete>
          </AdminOnly>
        )}
      </ModalControls>
    </>
  )
}

UserEdit.modalId = (userId: string) => `edit-user-${userId}`
